import { FC, useContext, useEffect } from "react";
import { Controller } from "react-hook-form";
import { Form, FormField, Input, ModalDialog, Numeric, TranslatableField } from "@rothoblaas/design-system";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useI18n from "../../../../hooks/useTranslations";
import {
  InsertLoadDurationClassBody,
  LoadDurationClass,
  UpdateLoadDurationClassBody,
} from "../../../../http/screwConnector/types/normativeTables";
import { useScrewConnectorLoadDurationClassesApi } from "../../../../http/screwConnector/useAdminNormativeTables";
import { translatableFieldProps } from "../../../../utils/translatableField";

interface LoadDurationClassFormBody {
  code: string | null;
  name: string | null;
  nameIt: string | null;
  nameDe: string | null;
  nameEs: string | null;
  nameFr: string | null;
  order: number | null;
}

interface LoadDurationClassFormProps {
  loadDurationClass?: LoadDurationClass;
  onClose: () => void;
}

const LoadDurationClassForm: FC<LoadDurationClassFormProps> = ({ loadDurationClass, onClose }) => {
  const i18n = useI18n();
  const t = i18n.translation.screwConnectorModule.normativeTables.loadDurationClasses;
  const dispatch = useContext(NotificationDispatch);
  const api = useScrewConnectorLoadDurationClassesApi();

  const defaultValues: LoadDurationClassFormBody = {
    code: loadDurationClass?.code ?? null,
    name: loadDurationClass?.name ?? null,
    nameIt: loadDurationClass?.nameIt ?? null,
    nameDe: loadDurationClass?.nameDe ?? null,
    nameEs: loadDurationClass?.nameEs ?? null,
    nameFr: loadDurationClass?.nameFr ?? null,
    order: loadDurationClass?.order ?? null,
  };

  const onSubmit = () => async (formData: LoadDurationClassFormBody) => {
    if (!formData.code || !formData.name) {
      return;
    }

    const body: UpdateLoadDurationClassBody = {
      name: formData.name,
      nameIt: formData.nameIt || null,
      nameDe: formData.nameDe || null,
      nameEs: formData.nameEs || null,
      nameFr: formData.nameFr || null,
      order: formData.order ?? 0,
    };

    try {
      if (loadDurationClass) {
        await api.updateLoadDurationClass(loadDurationClass.code, body);
      } else {
        const insertBody: InsertLoadDurationClassBody = { code: formData.code, ...body };
        await api.insertLoadDurationClass(insertBody);
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={
        loadDurationClass ? `${i18n.translation.common.edit} ${t.singular}` : `${i18n.translation.common.add} ${t.singular}`
      }
    >
      <Form<LoadDurationClassFormBody> submitText={i18n.translation.common.save} onSubmit={onSubmit} defaultValues={defaultValues}>
        {({ control, register, formState: { errors }, reset, watch, setValue }) => {
          useEffect(() => {
            reset(defaultValues);
          }, [loadDurationClass]);

          useEffect(() => {
            register("nameIt");
            register("nameDe");
            register("nameEs");
            register("nameFr");
          }, [register]);

          const values = watch();

          return (
            <FormFieldsContainer fullWidth>
              <FormField label={t.fields.code} error={errors.code} isOptional={false}>
                {({ labelId, isOptional, isInvalid }) => (
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={!!loadDurationClass}
                    {...register("code", { required: !isOptional, minLength: 1 })}
                  />
                )}
              </FormField>
              <TranslatableField
                {...translatableFieldProps("name", values, setValue, i18n.translation)}
                dialogTitle={t.fields.name}
              >
                <FormField label={t.fields.name} error={errors.name} isOptional={false}>
                  {({ labelId, isOptional, isInvalid }) => (
                    <Input id={labelId} isInvalid={isInvalid} {...register("name", { required: !isOptional, minLength: 1 })} />
                  )}
                </FormField>
              </TranslatableField>
              <Controller
                name="order"
                control={control}
                render={({ field, fieldState }) => (
                  <FormField label={t.fields.order} error={fieldState.error} isOptional>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        decimalScale={0}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
            </FormFieldsContainer>
          );
        }}
      </Form>
    </ModalDialog>
  );
};

export default LoadDurationClassForm;
